import React, { useMemo } from 'react';
import PDFHeader from './PDFHeader';
import { ItineraryItem, TripInfo } from '../../types/Quotation';
import { QUOTATION_BOOKING_NOTES } from './pdfQuotationNotes';

interface PDFPage2SummaryProps {
  tripInfo: TripInfo;
  itinerary: ItineraryItem[];
  visaDetails: Array<{ type: string; price: number; validity?: string; documents?: string[]; timeRequired?: string; currency?: string }>;
  insuranceDetails: Array<{ name: string; price: number; currency?: string; description?: string }>;
  renderedAt?: string;
}

const hasNamedInclusion = (inclusions: string[], names: string[]) => {
  const normalized = new Set(inclusions.map((value) => value.trim().toLowerCase()));
  return names.some((name) => normalized.has(name));
};

const totalNights = (tripInfo: TripInfo) =>
  tripInfo.places.reduce((sum, place) => sum + Math.max(0, Number(place.nights || 0)), 0);

const buildIncludedItems = (
  tripInfo: TripInfo,
  itinerary: ItineraryItem[],
  visaDetails: PDFPage2SummaryProps['visaDetails'],
  insuranceDetails: PDFPage2SummaryProps['insuranceDetails']
) => {
  const inclusions = tripInfo.inclusions || [];
  const nights = totalNights(tripInfo);
  const flightsCount = itinerary.reduce((sum, day) => sum + (day.flights || []).length, 0);
  const hotelsCount = itinerary.reduce((sum, day) => sum + (day.hotels || []).length, 0);
  const transfersCount = itinerary.reduce((sum, day) => sum + ((day.transfers || []).length || 0), 0);
  const activitiesCount = itinerary.reduce((sum, day) => sum + (day.activities || []).length, 0);

  const items: string[] = [];

  if (flightsCount > 0 || hasNamedInclusion(inclusions, ['flight', 'flights'])) {
    items.push('Flights as per selected itinerary.');
  }

  if (hotelsCount > 0 || hasNamedInclusion(inclusions, ['hotel', 'hotels'])) {
    items.push(
      nights > 0
        ? `${nights} night${nights > 1 ? 's' : ''} hotel accommodation as per itinerary.`
        : 'Hotel accommodation as per itinerary.'
    );
  }

  if (hasNamedInclusion(inclusions, ['meals'])) {
    items.push('Meals as mentioned in the package inclusions.');
  }

  if (transfersCount > 0 || hasNamedInclusion(inclusions, ['transfer', 'transfers'])) {
    const transferLabel = tripInfo.transferType ? `${tripInfo.transferType} transfers` : 'Transfers';
    items.push(`${transferLabel} as per itinerary.`);
  }

  if (activitiesCount > 0 || hasNamedInclusion(inclusions, ['activity', 'activities'])) {
    items.push('Sightseeing and activities mentioned in the itinerary.');
  }

  if (visaDetails.length > 0 || hasNamedInclusion(inclusions, ['visa'])) {
    items.push('Visa assistance / visa inclusion as selected in the quotation.');
  }

  if (insuranceDetails.length > 0 || hasNamedInclusion(inclusions, ['insurance'])) {
    items.push('Travel insurance as selected in the quotation.');
  }

  items.push('Applicable package taxes.');

  return items;
};

const PDFPage2Summary: React.FC<PDFPage2SummaryProps> = ({
  tripInfo,
  itinerary,
  visaDetails,
  insuranceDetails,
  renderedAt,
}) => {
  const includedItems = useMemo(
    () => buildIncludedItems(tripInfo, itinerary, visaDetails, insuranceDetails),
    [insuranceDetails, itinerary, tripInfo, visaDetails]
  );

  return (
    <div className="pdf-page">
      <PDFHeader renderedAt={renderedAt} />
      <section className="pdf-section">
        <div className="quotation-summary-grid">
          <div className="quotation-summary-card">
            <h3>Included in the Package</h3>
            <ul>
              {includedItems.map((item, index) => (
                <li key={`${item}-${index}`}>{item}</li>
              ))}
            </ul>
          </div>

          <div className="quotation-summary-card">
            <h3>Important Booking Notes</h3>
            <ul>
              {QUOTATION_BOOKING_NOTES.map((item, index) => (
                <li key={`${item}-${index}`}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PDFPage2Summary;
